import { useQuery, useQueryCache } from '@pinia/colada';
import { computed, MaybeRefOrGetter, shallowRef, toValue } from 'vue';

import { useCompanyQuery } from '@tallo/investor/company';
import { TimeFilter } from '@tallo/utility';

import { ShowingStatus } from '../interfaces/showing-status.enum';
import { ShowingDto } from '../interfaces/showing.interface';
import showingsService from '../services/showings.service';
import { showingsQueryKey } from './showings.query';

/** How many showings the list adds at a time (rise24 has 400+ in 30 days, ~1 s to load in one go) */
export const showingsListPageSize = 50;

export interface ShowingsList {
  showings: ShowingDto[];
  hasMore: boolean;
}

/**
 * The Showings page's list, read a page at a time. It is one cache entry under `showingsQueryKey`,
 * so the mutations' invalidation still refreshes it: a refresh reloads as many showings as the
 * list holds, from the top, in one request, rather than appending another page.
 */
export function useShowingsListQuery({
  timeFilter,
  propertyId,
  status,
}: {
  timeFilter?: MaybeRefOrGetter<TimeFilter | undefined>;
  propertyId?: MaybeRefOrGetter<string | undefined>;
  status?: MaybeRefOrGetter<ShowingStatus | undefined>;
} = {}) {
  const queryCache = useQueryCache();
  const companyQuery = useCompanyQuery();
  const isLoadingMore = shallowRef(false);

  const key = () => {
    const queryKey = [showingsQueryKey, 'list'];
    const timeFilterValue = toValue(timeFilter);
    const propertyIdValue = toValue(propertyId);
    const statusValue = toValue(status);

    if (timeFilterValue) {
      queryKey.push(`timeFilter:${timeFilterValue}`);
    }

    if (propertyIdValue) {
      queryKey.push(`propertyId:${propertyIdValue}`);
    }

    if (statusValue) {
      queryKey.push(`status:${statusValue}`);
    }

    return queryKey;
  };

  // One more than asked for tells whether anything is left, without a count query
  async function fetchShowings(offset: number, limit: number): Promise<ShowingsList> {
    const { data: company } = await companyQuery.refresh(true);
    const showings = await showingsService.getShowingsByCompany(company?.id as string, {
      timeFilter: toValue(timeFilter),
      propertyId: toValue(propertyId),
      status: toValue(status),
      limit: limit + 1,
      offset,
    });

    return { showings: showings.slice(0, limit), hasMore: showings.length > limit };
  }

  const query = useQuery({
    key,
    query: () => {
      const loaded = queryCache.getQueryData<ShowingsList>(key())?.showings.length ?? 0;

      return fetchShowings(0, Math.max(loaded, showingsListPageSize));
    },
  });

  const hasMore = computed(() => query.data.value?.hasMore ?? false);

  async function loadMore(): Promise<void> {
    const current = query.data.value;

    if (!current?.hasMore || isLoadingMore.value || query.asyncStatus.value === 'loading') {
      return;
    }

    const listKey = key();
    isLoadingMore.value = true;

    try {
      const next = await fetchShowings(current.showings.length, showingsListPageSize);
      // A tour booked since the last page shifts the offsets by one; skip what the list already has
      const known = new Set(current.showings.map(({ id }) => id));

      queryCache.setQueryData<ShowingsList>(listKey, {
        showings: [...current.showings, ...next.showings.filter(({ id }) => !known.has(id))],
        hasMore: next.hasMore,
      });
    } finally {
      isLoadingMore.value = false;
    }
  }

  return { ...query, hasMore, isLoadingMore, loadMore };
}
